import React, { useState, useEffect, Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu, Transition } from '@headlessui/react';
import { ChevronDownIcon } from '@heroicons/react/20/solid';
import { ChartBarIcon } from '@heroicons/react/24/outline';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';
import { apiClient } from '../../services/apiClient';

const EmployeeUsageChart = () => {
  const { t, i18n } = useTranslation();
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [usageStats, setUsageStats] = useState(null);
  const [availableYears, setAvailableYears] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Function to format month for X-axis
  const formatMonth = (month) => {
    return new Date(2000, month - 1, 1).toLocaleDateString(i18n.language, { month: 'long' });
  };

  // Custom tooltip that includes total
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      const total = payload.reduce((sum, entry) => sum + (parseInt(entry.value) || 0), 0);
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded shadow-lg">
          <p className="font-semibold">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }}>
              {entry.name}: {parseInt(entry.value) || 0}
            </p>
          ))}
          <p className="font-semibold mt-2 border-t pt-2">
            {t('statistics.total')}: {total}
          </p>
        </div>
      );
    }
    return null;
  };

  // Function to prepare data for chart
  const prepareChartData = () => {
    if (!usageStats || !Array.isArray(usageStats)) return [];
    
    // Get unique employee IDs and names
    const employees = [...new Set(usageStats.map(stat => ({
      id: stat.employee_id,
      name: stat.employee_name || stat.employee_id
    })))];
    
    // Group by month
    const groupedByMonth = usageStats.reduce((acc, curr) => {
      if (!acc[curr.month]) {
        acc[curr.month] = { 
          month: formatMonth(curr.month),
          monthNum: curr.month
        };
      }
      // Use employee_id as key for the chart
      acc[curr.month][curr.employee_id] = parseInt(curr.count) || 0;
      return acc;
    }, {});

    // Convert to array and sort by month
    return Object.values(groupedByMonth)
      .sort((a, b) => a.monthNum - b.monthNum)
      .map(({ monthNum, ...rest }) => rest);
  };

  // Generate colors for employees (using a consistent color palette)
  const generateEmployeeColors = () => {
    if (!usageStats || !Array.isArray(usageStats)) return {};
    
    const employees = [...new Set(usageStats.map(stat => stat.employee_id))];
    const colors = [
      '#34a853',  // green
      '#ffb366',  // pastel orange
      '#61dade',  // cyan
      '#7f1ac6',  // purple
      '#4facfe',  // light blue
      '#43e97b',  // light green
      '#f093fb',  // magenta
      '#4285f4',  // blue
      '#fbbc05',  // yellow/orange
      '#ea4335',  // red
      '#ff6b9d',  // pink
      '#c471ed',  // violet
      '#fa709a',  // rose
      '#fee140',  // yellow
      '#30cfd0'   // turquoise
    ];
    
    const colorMap = {};
    employees.forEach((employeeId, index) => {
      colorMap[employeeId] = colors[index % colors.length];
    });
    
    return colorMap;
  };

  // Fetch usage statistics
  useEffect(() => {
    const fetchUsageStats = async () => {
      try {
        setIsLoading(true);
        setError(null);
        const data = await apiClient.get(
          `/api/statistics/usage-by-employee?year=${selectedYear}`
        );

        setUsageStats(Array.isArray(data.usageStats) ? data.usageStats : []);
        setAvailableYears(Array.isArray(data.availableYears) ? data.availableYears : []);
      } catch (err) {
        console.error('Error fetching employee usage statistics:', err);
        setError(t('statistics.errors.fetchFailed', 'Failed to fetch statistics'));
        setUsageStats([]);
        setAvailableYears([]);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUsageStats();
  }, [selectedYear, t]);

  const chartData = prepareChartData();
  const employeeColors = generateEmployeeColors();
  const employees = [...new Set((usageStats || []).map(stat => stat.employee_id))];

  // Empty state component
  const EmptyState = ({ message }) => (
    <div className="flex flex-col items-center justify-center h-64 text-gray-500">
      <ChartBarIcon className="w-16 h-16 mb-4" />
      <p className="text-lg">{message}</p>
    </div>
  );

  return (
    <div className="mt-6 pt-6 border-t border-gray-300">
      <div className="px-6 py-4">
        <h3 className="text-lg font-medium text-gray-900 mb-4">
          {t('statistics.usageByEmployee.title', 'Usage par employé')}
        </h3>
      </div>
      <div className="px-6 py-4">
        <div className="flex justify-end mb-4">
          <Menu as="div" className="relative inline-block text-left">
            {({ open }) => (
              <>
                <Menu.Button className="inline-flex w-56 justify-between items-center rounded-md bg-[rgb(197,214,248)] px-4 py-2 text-sm font-medium text-gray-700 hover:bg-[rgb(187,204,238)] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 focus:ring-offset-gray-100">
                  {selectedYear}
                  <ChevronDownIcon 
                    className={`ml-2 h-5 w-5 text-gray-500 transition-transform duration-200 ${
                      open ? 'transform rotate-180' : ''
                    }`} 
                    aria-hidden="true" 
                  />
                </Menu.Button>

                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="opacity-0 scale-95"
                  enterTo="opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="opacity-100 scale-100"
                  leaveTo="opacity-0 scale-95"
                >
                  <Menu.Items className="absolute right-0 z-10 mt-2 w-56 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                    <div className="py-1">
                      {availableYears.length > 0 ? (
                        availableYears.map((year) => (
                          <Menu.Item key={year}>
                            {({ active }) => (
                              <button
                                onClick={() => setSelectedYear(year)}
                                className={`${
                                  active ? 'bg-[rgb(197,214,248)]' : ''
                                } ${
                                  year === selectedYear ? 'bg-[rgb(197,214,248)]' : ''
                                } group flex w-full items-center px-4 py-2 text-sm text-gray-700`}
                              >
                                {year}
                              </button>
                            )}
                          </Menu.Item>
                        ))
                      ) : (
                        <div className="px-4 py-2 text-sm text-gray-500">
                          {t('statistics.noYearsAvailable', 'No years available')}
                        </div>
                      )}
                    </div>
                  </Menu.Items>
                </Transition>
              </>
            )}
          </Menu>
        </div>
        <div className="h-80">
          {isLoading ? (
            <div className="flex items-center justify-center h-full">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : error ? (
            <div className="text-red-600 p-4 text-center">
              {error}
            </div>
          ) : chartData.length === 0 ? (
            <EmptyState message={t('statistics.noData', 'No data available')} />
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={chartData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="month" />
                <YAxis />
                <Tooltip content={<CustomTooltip />} />
                <Legend />
                {employees.map(employeeId => {
                  const employeeName = usageStats.find(s => s.employee_id === employeeId)?.employee_name || employeeId;
                  return (
                    <Bar
                      key={employeeId}
                      dataKey={employeeId}
                      name={employeeName}
                      stackId="a"
                      fill={employeeColors[employeeId]}
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>
      </div>
    </div>
  );
};

export default EmployeeUsageChart;

